import React, { useEffect, useRef, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";
import { maskDigits, isoFromDisplay, displayFromIso, maskTime, isValidTime, pad2 } from "@/lib/dateFormat";

const NOW = new Date();
const DEFAULT_FROM_YEAR = 1950;
const DEFAULT_TO_YEAR = NOW.getFullYear() + 10;

// Shared date input used for EVERY date on the site.
// value/onChange speak ISO "YYYY-MM-DD" (onChange(null) when cleared).
export const DateField = ({
  value, onChange, placeholder = "DD/MM/YYYY", helper = "DD/MM/YYYY", testid,
  disabled = false, minDate, maxDate, disableFuture = false,
  fromYear = DEFAULT_FROM_YEAR, toYear = DEFAULT_TO_YEAR, className = "",
}) => {
  const [display, setDisplay] = useState(() => displayFromIso(value));
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const focused = useRef(false);

  useEffect(() => { if (!focused.current) setDisplay(displayFromIso(value)); }, [value]);

  const commitFromText = (masked) => {
    const digits = masked.replace(/\D/g, "");
    if (digits.length === 0) { onChange?.(null); return; }
    if (digits.length === 8) { const iso = isoFromDisplay(masked); if (iso) onChange?.(iso); }
  };

  const handleInput = (e) => {
    const masked = maskDigits(e.target.value);
    setDisplay(masked);
    setError("");
    commitFromText(masked);
  };
  const handleBlur = () => {
    focused.current = false;
    const digits = display.replace(/\D/g, "");
    if (digits.length === 0) { setError(""); return; }
    setError(digits.length === 8 && isoFromDisplay(display) ? "" : "Enter a valid date as DD/MM/YYYY");
  };

  const toDate = maxDate ? new Date(`${maxDate}T00:00:00`) : null;
  const futureCap = disableFuture ? new Date(NOW.getFullYear(), NOW.getMonth(), NOW.getDate()) : null;
  const after = toDate && futureCap ? (toDate < futureCap ? toDate : futureCap) : (toDate || futureCap);
  const before = minDate ? new Date(`${minDate}T00:00:00`) : null;
  const disabledMatcher = {};
  if (before) disabledMatcher.before = before;
  if (after) disabledMatcher.after = after;

  const selected = value && /^\d{4}-\d{2}-\d{2}/.test(String(value)) ? new Date(`${String(value).slice(0, 10)}T00:00:00`) : undefined;
  const pick = (d) => {
    if (!d) return;
    const iso = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    setDisplay(displayFromIso(iso));
    setError("");
    onChange?.(iso);
    setOpen(false);
  };

  return (
    <div>
      <div className="relative">
        <input
          data-testid={testid}
          type="text"
          inputMode="numeric"
          maxLength={10}
          placeholder={placeholder}
          value={display}
          disabled={disabled}
          onFocus={() => { focused.current = true; }}
          onChange={handleInput}
          onBlur={handleBlur}
          className={`flex h-10 w-full rounded-md border border-gmoaa-border-subtle bg-white px-3 py-2 pr-10 text-sm text-gmoaa-text-dark placeholder:text-gmoaa-text-muted focus:outline-none focus:ring-2 focus:ring-gmoaa-green/30 disabled:opacity-70 ${className}`}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Open calendar"
              disabled={disabled}
              data-testid={testid ? `${testid}-calendar` : undefined}
              onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); setOpen(true); } }}
              className="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded text-gmoaa-text-muted hover:bg-gmoaa-green-light hover:text-gmoaa-green disabled:opacity-50"
            >
              <CalendarIcon size={16} />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              captionLayout="dropdown-buttons"
              fromYear={fromYear}
              toYear={toYear}
              selected={selected}
              defaultMonth={selected || (after && after < NOW ? after : NOW)}
              onSelect={pick}
              disabled={Object.keys(disabledMatcher).length ? disabledMatcher : undefined}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
      {error
        ? <p className="mt-1 text-xs text-gmoaa-red" data-testid={testid ? `${testid}-error` : undefined}>{error}</p>
        : helper ? <p className="mt-1 text-[11px] text-gmoaa-text-muted">{helper}</p> : null}
    </div>
  );
};

// Two DateFields; value={from,to}; the "to" field cannot be before "from".
export const DateRangeField = ({ value = {}, onChange, testid, ...rest }) => {
  const set = (k) => (iso) => onChange?.({ ...value, [k]: iso });
  return (
    <div className="grid grid-cols-2 gap-2">
      <DateField value={value.from} onChange={set("from")} testid={testid ? `${testid}-from` : undefined} {...rest} />
      <DateField value={value.to} onChange={set("to")} minDate={value.from} testid={testid ? `${testid}-to` : undefined} {...rest} />
    </div>
  );
};

// 24-hour HH:MM time field.
export const TimeField = ({ value, onChange, testid, disabled = false, className = "" }) => {
  const [display, setDisplay] = useState(value || "");
  const [error, setError] = useState("");
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setDisplay(value || ""); }, [value]);
  const handleInput = (e) => {
    const masked = maskTime(e.target.value);
    setDisplay(masked); setError("");
    if (masked.replace(/\D/g, "").length === 0) onChange?.(null);
    else if (masked.length === 5 && isValidTime(masked)) onChange?.(masked);
  };
  return (
    <div>
      <input
        data-testid={testid}
        type="text"
        inputMode="numeric"
        maxLength={5}
        placeholder="HH:MM"
        value={display}
        disabled={disabled}
        onFocus={() => { focused.current = true; }}
        onChange={handleInput}
        onBlur={() => { focused.current = false; setError(display && !isValidTime(display) ? "Enter a valid time as HH:MM (24h)" : ""); }}
        className={`flex h-10 w-full rounded-md border border-gmoaa-border-subtle bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gmoaa-green/30 disabled:opacity-70 ${className}`}
      />
      {error ? <p className="mt-1 text-xs text-gmoaa-red">{error}</p> : <p className="mt-1 text-[11px] text-gmoaa-text-muted">HH:MM</p>}
    </div>
  );
};
